/**
 * The report id shapes that can reach the frontend.
 *
 * `/reports/:template` is an overloaded route: the param is either a v1
 * template key (`pipeline-health`, `custom`, …) or the id of a first-class v2
 * report_definition. Those ids are Relay global ids — base64 of
 * `ReportDefinition:<pk>` — so the sequential Postgres pk never appears in a
 * URL. That's the campaign convention (crm/api/campaign.py); the backend
 * decodes via reports/service.py `report_pk`.
 *
 * Bare integers still count as report ids: favorites saved before the switch
 * stored the raw pk in `reportDefinitionId`, and the backend's decoder falls
 * through to `int()` for them.
 *
 * Note on URL safety: base64 of an ASCII payload below 0x7B can never emit
 * `+` or `/` (the top sextet value reachable from those bytes is 61), so a
 * report global id is always `[A-Za-z0-9]+` plus `=` padding — all legal in a
 * path segment, no escaping needed.
 */

const NODE_TYPE = "ReportDefinition";

/** `[A-Za-z0-9+/]` with optional padding — the base64 alphabet. */
const BASE64_SHAPE = /^[A-Za-z0-9+/]+={0,2}$/;
const GLOBAL_ID_BODY = new RegExp(`^${NODE_TYPE}:(\\d+)$`);
const BARE_PK = /^\d+$/;

function decodeBase64(value: string): string | null {
  try {
    return atob(value);
  } catch {
    // Wrong length or alphabet — not a global id.
    return null;
  }
}

/**
 * The report_definition pk this id addresses, or null when the value isn't a
 * report id at all (a v1 template key, or junk typed into the URL bar).
 */
export function reportDefinitionPk(id: string | null | undefined): string | null {
  if (!id) return null;
  if (BARE_PK.test(id)) return id;
  if (!BASE64_SHAPE.test(id)) return null;
  const decoded = decodeBase64(id);
  return decoded ? (GLOBAL_ID_BODY.exec(decoded)?.[1] ?? null) : null;
}

/**
 * True when the `/reports/:template` param addresses a v2 report definition
 * rather than a v1 template key.
 */
export function isReportDefinitionId(id: string | null | undefined): boolean {
  return reportDefinitionPk(id) !== null;
}

/**
 * Comparison key for a saved-report favorite, which is keyed on whatever id
 * shape was current when it was starred. Without this, a report starred before
 * the switch (entityId `"42"`) reads as unstarred once the API starts handing
 * out `"UmVwb3J0RGVmaW5pdGlvbjo0Mg=="`, and re-starring writes a second row.
 * Non-report ids — and v1 template favorites like `report-pipeline-health` —
 * pass through untouched.
 */
export function reportFavoriteKey(id: string): string {
  return reportDefinitionPk(id) ?? id;
}
